import { MouseEventHandler, forwardRef } from "react";
import moment from "moment";
import { useNavigate } from "react-router";
import { Box, IconButton, Slide, Snackbar, Stack, Alert as MuiAlert } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Text } from "@imminently/react/ui/core/text";
import { INotification, INotificationType, INotificationsSelector, useNotifications } from "@imminently/react/store/notifications";

import { Theme, css } from "@/theme";
import { HeartIcon } from "@/assets/icons";
import { useTheme } from "@emotion/react";
import { Icon } from "./Icon";
import { getPatientRoute } from "@/pages";
import { PatientNotification } from "@/global";

const useNotificationColor = (type: INotificationType) => {
  const theme = useTheme();
  return theme.palette[type];
};

const styles = {
  root: (theme: Theme) => css`
    color: var(--color);
    background-color: var(--background-color);
    box-shadow: ${theme.shadows[1]};
    border-radius: 0.5rem;
    padding: 1rem;
    min-width: 24rem;
    cursor: pointer;
  `,
  icon: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    color: var(--icon-color);
    background-color: var(--icon-background-color);
  `,
  close: css`
    position: relative;
    top: -0.5rem;
    right: -0.5rem;
    align-self: flex-start;
  `,
};

export type NotificationProps = {
  notification: PatientNotification;
  dismiss: () => void;
};

const Notification = forwardRef<any, NotificationProps>(({ notification, dismiss }, ref) => {
  const { patientName, encounterId, message, when, type } = notification;
  const color = useNotificationColor(type ?? "info");
  const navigate = useNavigate();

  const vars = {
    "--color": color[900],
    "--background-color": color[50],
    "--icon-color": color.contrastText,
    "--icon-background-color": color.main,
  } as React.CSSProperties;

  const timeAgo = moment(when).fromNow();

  const onClick = () => {
    navigate(getPatientRoute(encounterId));
    dismiss();
  };

  const dismissNotification: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dismiss();
  };

  return (
    <Stack ref={ref} style={vars} css={styles.root} onClick={onClick}>
      <Stack direction="row" gap={2}>
        <Box css={styles.icon}>
          <HeartIcon />
        </Box>
        <Stack flex={1}>
          <Text mb={1} variant="caption">{timeAgo}</Text>
          <Text variant="h4">{message}</Text>
          <Text variant="body1">{patientName}</Text>
        </Stack>
        <IconButton css={styles.close} onClick={dismissNotification}>
          <Icon icon="close" />
        </IconButton>
      </Stack>
    </Stack>
  );
});
Notification.displayName = "Notification";

type AlertProps = { notification?: INotification };

const Alert = forwardRef<any, AlertProps>(({ notification }, ref) => (
  <MuiAlert
    ref={ref}
    elevation={6}
    variant="filled"
    severity={notification?.type}
    onClose={close}
  >
    {notification?.message}
  </MuiAlert>
));
Alert.displayName = "Alert";

const TransitionRight = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="right" ref={ref} {...props} />;
});

export type AppSnackbarProps = { selector: INotificationsSelector };

export const AppSnackbar = ({ selector }: AppSnackbarProps) => {
  const { notification, open, close } = useNotifications(selector);

  const isPatientNotification = !!(notification as PatientNotification)?.encounterId;
  const dismiss = () => close(null);

  return (
    <Snackbar
      open={open}
      onClose={close}
      disableWindowBlurListener
      autoHideDuration={notification?.duration}
      TransitionComponent={TransitionRight}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      {
        isPatientNotification
          ? <Notification notification={notification as PatientNotification} dismiss={dismiss} />
          : <Alert notification={notification} />
      }
    </Snackbar>
  );
};